import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { CacheGetItem, CacheRemoveItem, CacheSetItem, LEGACY_COOKIE_KEYS, STORAGE_KEYS, StorageRemoveItem, WEBSITE_CACHE_AGE } from "../admin/components/StorageComp";

const WebsiteContext = createContext(null);

// Re-check for template/company changes at most this often when the tab regains focus
const FOCUS_CHECK_INTERVAL = 60 * 1000;

const getHostDomain = () => {
    let domain = window.location.hostname;
    if (window.location.port) {
        domain += ":" + window.location.port;
    }
    return domain;
};

// The API answered that no company uses this domain (not just a missing route, e.g. a backend
// not yet deployed with website-version)
const isCompanyNotFound = (error) => error?.response?.status === 404 && error?.response?.data?.status === "ERROR";

export const WebsiteProvider = ({ children }) => {
    const [currentDomain, setCurrentDomain] = useState('');
    const [websiteData, setWebsiteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const lastCheckRef = useRef(0);

    // Company no longer exists for this host: drop anything cached and show the default site
    const clearWebsite = () => {
        CacheRemoveItem(STORAGE_KEYS.WEBSITE);
        setWebsiteData(null);
    };

    const fetchWebsite = async (domain) => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API_URL + "v1/website/website-by-domain",
                {
                    // _t keeps any browser/proxy cache from answering with an old copy
                    params: { domain, _t: Date.now() },
                }
            );

            const data = response.data.data || null;

            setWebsiteData(data);

            if (data) {
                CacheSetItem(STORAGE_KEYS.WEBSITE, data, WEBSITE_CACHE_AGE);
            }

            return data;
        } catch (error) {
            if (isCompanyNotFound(error)) clearWebsite();
            return null;
        }
    };

    // Every template/company save bumps updated_at; only download the full config when it changed
    const checkForChanges = async (domain) => {
        lastCheckRef.current = Date.now();
        const cache = CacheGetItem(STORAGE_KEYS.WEBSITE);
        if (cache?.domain !== domain) return fetchWebsite(domain);

        try {
            const response = await axios.get(
                process.env.REACT_APP_API_URL + "v1/website/website-version",
                { params: { domain, _t: Date.now() } }
            );
            if (response.data?.data?.updated_at !== cache.updated_at) {
                return fetchWebsite(domain);
            }
            return cache;
        } catch (error) {
            if (isCompanyNotFound(error)) {
                clearWebsite();
                return null;
            }
            // Version check failed (e.g. offline): fall back to the full fetch
            return fetchWebsite(domain);
        }
    };

    useEffect(() => {
        const domain = getHostDomain();

        const loadWebsite = async () => {
            try {
                setCurrentDomain(domain);

                // clear cookies from before the key rename (website data in cookies
                // was too large and got dropped by Safari)
                LEGACY_COOKIE_KEYS.forEach((key) => StorageRemoveItem(key));

                // main domain -> no website config needed
                if (domain === process.env.REACT_APP_BASE_DOMAIN) {
                    setLoading(false);
                    return;
                }

                const cache = CacheGetItem(STORAGE_KEYS.WEBSITE);

                // show cached data immediately, then make sure it's still current
                if (cache?.domain === domain) {
                    setWebsiteData(cache);
                    setLoading(false);
                    await checkForChanges(domain);
                } else {
                    await fetchWebsite(domain);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        };

        loadWebsite();

        // A page left open picks up template/company changes when the visitor comes back to it
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            if (domain === process.env.REACT_APP_BASE_DOMAIN) return;
            if (Date.now() - lastCheckRef.current < FOCUS_CHECK_INTERVAL) return;
            checkForChanges(domain);
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <WebsiteContext.Provider
            value={{
                websiteData,
                setWebsiteData,
                currentDomain,
                setCurrentDomain,
                loading,
                refreshWebsite: fetchWebsite,
            }}
        >
            {children}
        </WebsiteContext.Provider>
    );
};

export const useWebsite = () => {
    return useContext(WebsiteContext);
};
